import { AddOn } from "@/lib/index";
import { useCart } from "@/hooks/useCart";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";

interface AddOnCardProps {
  addOn: AddOn;
}

/**
 * AddOnCard component displays a single add-on item with price and WhatsApp order action.
 * Follows the project's design philosophy of clarity and tactile micro-interactions.
 */
export function AddOnCard({ addOn }: AddOnCardProps) {
  const { addItem } = useCart();

  const handleAddToCart = () => {
    addItem(addOn, 'addon');
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
    >
      <Card className="overflow-hidden border-border bg-card hover:border-primary/50 transition-colors">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-foreground text-base">
              {addOn.name}
            </h3>
            {addOn.description && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {addOn.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <span className="font-mono font-bold text-primary text-lg">
              RM {addOn.price}
            </span>
            
            <Button
              size="icon"
              variant="secondary"
              className="rounded-full w-10 h-10 bg-accent hover:bg-primary hover:text-primary-foreground transition-all shrink-0"
              onClick={handleAddToCart}
              aria-label={`Add ${addOn.name} to order`}
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
